"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { submitFeedback, type FeedbackScore } from "@/lib/feedback/submitFeedback";
import type { FeedbackLabels } from "@/queries/feedbackLabelsQueries";
import { useLogin } from "@/context/login/LoginContext";
import { isUserAuthenticated } from "@/context/login/reducer";
import { usePageFeedback } from "./PageFeedbackContext";
import "./FeedbackBar.css";
type Status = "idle" | "submitting" | "success" | "error";

/**
 * Modus `thumbs-up` / `thumbs-down` (regular variant), inlined from
 * @trimble-oss/modus-icons-css. The `modus-icon-*` classes the TD nav uses are
 * CSS masks defined in two ~1MB CDN stylesheets that only TrimbleDeveloperHeader
 * links; this bar renders whenever Contentful supplies feedback labels, which
 * includes pages that never mount that header, so it can't depend on them.
 */
const THUMB_ICON_PATH = {
  up: "M32 104h48v104H32a8 8 0 0 1-8-8v-88a8 8 0 0 1 8-8M80 104l40-80a32 32 0 0 1 32 32v24h64a16 16 0 0 1 15.87 18l-12 96A16 16 0 0 1 204 208H80",
  down: "M32 48h48v104H32a8 8 0 0 1-8-8V56a8 8 0 0 1 8-8M80 152l40 80a32 32 0 0 0 32-32v-24h64a16 16 0 0 0 15.87-18l-12-96A16 16 0 0 0 204 48H80"
} as const;
function ThumbIcon(t0) {
  const $ = _c(2);
  const {
    direction
  } = t0;
  const t1 = THUMB_ICON_PATH[direction];
  let t2;
  if ($[0] !== t1) {
    t2 = <svg className="feedback-bar__rating-icon" viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path d={t1} fill="none" stroke="currentColor" strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}
type FeedbackBarProps = {
  labels?: FeedbackLabels | null;
  actionBlockDisplayed?: boolean | null;
};

/**
 * "Was this page helpful?" bar for the Trimble Developer build. Rendered
 * sitewide between #page-content and the footer (see [lang]/layout.tsx),
 * gated to NEXT_PUBLIC_SITE_MACHINE_NAME === "trimble-developer". Submits to
 * the Trimble Feedback Service (score: 5 = thumbs up, 1 = thumbs down).
 *
 * Text is sourced from siteConfig.feedbackLabels (see feedbackLabelsQueries)
 * so it can be managed/translated in Contentful, with hardcoded fallbacks.
 *
 * Opt-in per page: the layout supplies the labels, but the bar only renders
 * once the page publishes `showFeedbackComponent` from its seo entry via
 * PageFeedbackOverride. Pages that never mount that marker stay opted out.
 */
export default function FeedbackBar(t0) {
  const $ = _c(56);
  const {
    labels,
    actionBlockDisplayed
  } = t0;
  const {
    showFeedback,
    pageHasActionBlock
  } = usePageFeedback();
  const hasActionBlockSpacing = actionBlockDisplayed === true || pageHasActionBlock === true;
  const [loginState] = useLogin() ?? [];
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState("");
  const [status, setStatus] = useState("idle");
  if (!showFeedback) {
    return null;
  }
  let t1;
  if ($[0] !== loginState) {
    t1 = isUserAuthenticated(loginState) ? loginState?.user?.email : undefined;
    $[0] = loginState;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tidEmail = t1;
  const scoreQuestion = labels?.feedbackFormScoreQuestion || "Was this page helpful?";
  const feedbackLabel = labels?.feedbackFormFeedbackLabel || "Feedback";
  const goodLabel = labels?.feedbackFormGoodLabel || "Thumbs up";
  const badLabel = labels?.feedbackFormBadLabel || "Thumbs down";
  const commentPlaceholder = labels?.feedbackFormCommentPlaceholder || "Any additional comments? (optional)";
  const submitButtonText = labels?.feedbackFormSubmitButton || "Submit";
  const cancelButtonText = labels?.feedbackFormCancelButton || "Cancel";
  const loadingText = labels?.feedbackLoadingText || "Submitting\u2026";
  const successMessage = labels?.feedbackFormSuccessMessage || "Thanks for your feedback!";
  const errorMessage = labels?.feedbackFormErrorMessage || "Something went wrong. Please try again.";
  const feedbackBarWrapperClasses = `feedback-bar ${hasActionBlockSpacing ? "feedback-bar--with-action-block" : ""}`;
  let t2;
  if ($[2] !== comment || $[3] !== score || $[4] !== status || $[5] !== tidEmail) {
    t2 = async () => {
      if (!score || status === "submitting") {
        return;
      }
      setStatus("submitting");
      const ok = await submitFeedback(score, comment, tidEmail);
      setStatus(ok ? "success" : "error");
    };
    $[2] = comment;
    $[3] = score;
    $[4] = status;
    $[5] = tidEmail;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleSubmit = t2;
  let t3;
  if ($[7] !== status) {
    t3 = () => {
      if (status === "submitting") {
        return;
      }
      setScore(null);
      setComment("");
      setStatus("idle");
    };
    $[7] = status;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleCancel = t3;
  if (status === "success") {
    let t4;
    if ($[9] !== successMessage) {
      t4 = <p className="feedback-bar__message">{successMessage}</p>;
      $[9] = successMessage;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== feedbackBarWrapperClasses || $[12] !== t4) {
      t5 = <div className={feedbackBarWrapperClasses}>{t4}</div>;
      $[11] = feedbackBarWrapperClasses;
      $[12] = t4;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  let t4;
  if ($[14] !== scoreQuestion) {
    t4 = <span className="feedback-bar__prompt">{scoreQuestion}</span>;
    $[14] = scoreQuestion;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const t5 = score === 5;
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setScore(5);
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const t7 = `feedback-bar__rating-btn${score === 5 ? " feedback-bar__rating-btn--active" : ""}`;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ThumbIcon direction="up" />;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== goodLabel || $[19] !== t5 || $[20] !== t7) {
    t9 = <button type="button" aria-label={goodLabel} aria-pressed={t5} onClick={t6} className={t7}>{t8}</button>;
    $[18] = goodLabel;
    $[19] = t5;
    $[20] = t7;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = score === 1;
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setScore(1);
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const t12 = `feedback-bar__rating-btn${score === 1 ? " feedback-bar__rating-btn--active" : ""}`;
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ThumbIcon direction="down" />;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== badLabel || $[25] !== t10 || $[26] !== t12) {
    t14 = <button type="button" aria-label={badLabel} aria-pressed={t10} onClick={t11} className={t12}>{t13}</button>;
    $[24] = badLabel;
    $[25] = t10;
    $[26] = t12;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== feedbackLabel || $[29] !== t14 || $[30] !== t9) {
    t15 = <div className="feedback-bar__ratings" role="group" aria-label={feedbackLabel}>{t9}{t14}</div>;
    $[28] = feedbackLabel;
    $[29] = t14;
    $[30] = t9;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t15 || $[33] !== t4) {
    t16 = <div className="feedback-bar__ratings-wrapper">{t4}{t15}</div>;
    $[32] = t15;
    $[33] = t4;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== cancelButtonText || $[36] !== comment || $[37] !== commentPlaceholder || $[38] !== feedbackLabel || $[39] !== handleCancel || $[40] !== handleSubmit || $[41] !== loadingText || $[42] !== score || $[43] !== status || $[44] !== submitButtonText) {
    t17 = score !== null && <div className="feedback-bar__feedback-wrapper">{feedbackLabel && <span className="feedback-bar__feedback-label">{feedbackLabel}</span>}<textarea value={comment} onChange={e => setComment(e.target.value)} placeholder={commentPlaceholder} maxLength={500} rows={2} className="feedback-bar__comment" /><div className="feedback-bar__actions"><button type="button" onClick={handleCancel} disabled={status === "submitting"} className="feedback-bar__cancel">{cancelButtonText}</button><button type="button" onClick={handleSubmit} disabled={status === "submitting"} className="feedback-bar__submit">{status === "submitting" ? loadingText : submitButtonText}</button></div></div>;
    $[35] = cancelButtonText;
    $[36] = comment;
    $[37] = commentPlaceholder;
    $[38] = feedbackLabel;
    $[39] = handleCancel;
    $[40] = handleSubmit;
    $[41] = loadingText;
    $[42] = score;
    $[43] = status;
    $[44] = submitButtonText;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== errorMessage || $[47] !== status) {
    t18 = status === "error" && <span className="feedback-bar__error">{errorMessage}</span>;
    $[46] = errorMessage;
    $[47] = status;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t16 || $[50] !== t17 || $[51] !== t18) {
    t19 = <div className="feedback-bar__inner">{t16}{t17}{t18}</div>;
    $[49] = t16;
    $[50] = t17;
    $[51] = t18;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== feedbackBarWrapperClasses || $[54] !== t19) {
    t20 = <div className={feedbackBarWrapperClasses}>{t19}</div>;
    $[53] = feedbackBarWrapperClasses;
    $[54] = t19;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  return t20;
}